/**
 * JobCardStepIndicator — sticky horizontal strip showing the 5 steps of
 * the mechanic workflow with completion checkmarks. Each pill is a
 * click-to-jump anchor so a mechanic on iPad can move between steps
 * without scrolling the whole modal.
 *
 * Touch targets are 44px+ tall to satisfy Apple HIG (iPad floor use).
 */
import { Car, Wrench, AlertTriangle, FileText, ClipboardCheck, Check } from 'lucide-react';

const STEPS = [
  { num: 1, key: 'vehicle-info',   anchor: 'jc-step-1-vehicle-info',   label: 'Vehicle Info',  Icon: Car },
  { num: 2, key: 'tasks',          anchor: 'jc-step-2-tasks',          label: 'Tasks',         Icon: Wrench },
  { num: 3, key: 'findings',       anchor: 'jc-step-3-findings',       label: 'Findings',      Icon: AlertTriangle },
  { num: 4, key: 'qc',             anchor: 'jc-step-4-qc',             label: 'Quality Check', Icon: ClipboardCheck },
  { num: 5, key: 'work-performed', anchor: 'jc-step-5-work-performed', label: 'Work Done',     Icon: FileText },
];

export default function JobCardStepIndicator({ completion }) {
  /**
   * `completion` is a 1..5-keyed bool map indicating each step's done state.
   * Parent (JobCardDetailModal) derives it from JC state.
   */
  const jumpTo = (anchor) => {
    const el = document.querySelector(`[data-testid="${anchor}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <nav
      className="sticky top-0 z-10 -mx-4 px-4 sm:-mx-6 sm:px-6 py-2 bg-white/95 backdrop-blur border-b border-slate-200 mb-2"
      data-testid="jc-step-indicator"
      aria-label="Job card workflow steps"
      aria-live="polite"
    >
      <ol className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto no-scrollbar">
        {STEPS.map((s, idx) => {
          const done = !!completion?.[s.num];
          return (
            <li key={s.key} className="flex items-center flex-shrink-0">
              <button
                type="button"
                onClick={() => jumpTo(s.anchor)}
                className={`flex items-center gap-1.5 sm:gap-2 min-h-11 px-2.5 sm:px-3.5 rounded-full text-xs sm:text-sm font-medium transition-colors touch-manipulation
                  ${done
                    ? 'bg-emerald-50 text-emerald-800 border border-emerald-300 hover:bg-emerald-100'
                    : 'bg-slate-50 text-slate-700 border border-slate-300 hover:bg-slate-100'}`}
                data-testid={`step-pill-${s.num}`}
                aria-current={done ? 'true' : 'false'}
                aria-label={`Jump to Step ${s.num} ${s.label}${done ? ' (complete)' : ''}`}
              >
                <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-bold
                  ${done ? 'bg-emerald-600 text-white' : 'bg-slate-700 text-white'}`}>
                  {done ? <Check className="h-3.5 w-3.5" /> : s.num}
                </span>
                <s.Icon className={`h-3.5 w-3.5 ${done ? 'text-emerald-700' : 'text-slate-600'} hidden sm:inline`} />
                <span className="whitespace-nowrap">{s.label}</span>
              </button>
              {idx < STEPS.length - 1 && (
                <span
                  aria-hidden="true"
                  className={`hidden sm:inline-block w-3 h-[2px] mx-0.5 ${done ? 'bg-emerald-400' : 'bg-slate-300'}`}
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
